import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr, sign, download } from "@/lib/api";
import { Seg } from "@/pages/Dashboard";

const ym = (d) => d.slice(0, 7);
const shift = (m, n) => { const [y, mo] = m.split("-").map(Number); const d = new Date(Date.UTC(y, mo - 1 + n, 1)); return d.toISOString().slice(0, 7); };
const label = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" });

function weeks(month) {
  const [y, mo] = month.split("-").map(Number);
  const days = [];
  for (let d = new Date(Date.UTC(y, mo - 1, 1)); d.getUTCMonth() === mo - 1; d.setUTCDate(d.getUTCDate() + 1)) {
    const wd = d.getUTCDay();
    if (wd >= 1 && wd <= 5) days.push({ iso: d.toISOString().slice(0, 10), wd });
  }
  const out = [];
  let row = Array(5).fill(null);
  days.forEach((x, i) => {
    row[x.wd - 1] = x;
    if (x.wd === 5 || i === days.length - 1) { out.push(row); row = Array(5).fill(null); }
  });
  return out;
}

function DayCell({ day, v, max }) {
  if (!day) return <div className="h-20 rounded-md" />;
  const a = v ? Math.min(Math.abs(v.net) / (max || 1), 1) : 0;
  const bg = !v ? "transparent" : v.net > 0 ? `rgba(16,185,129,${0.08 + a * 0.32})` : v.net < 0 ? `rgba(239,68,68,${0.08 + a * 0.32})` : "rgba(148,163,184,0.08)";
  return (
    <div className="h-20 rounded-md border border-[#1E293B] p-2 flex flex-col transition-colors hover:border-cyan-500/40" style={{ background: bg }} data-testid={`cal-day-${day.iso}`}>
      <span className="text-[10px] num text-slate-500">{Number(day.iso.slice(8))}</span>
      {v && <>
        <span className={`num text-sm font-semibold mt-auto ${sign(v.net)}`} data-testid={`cal-net-${day.iso}`}>{inr(v.net)}</span>
        <span className="text-[10px] text-slate-400 truncate">{v.trades} trade{v.trades === 1 ? "" : "s"}{v.sector ? ` · ${v.sector}` : ""}</span>
      </>}
    </div>
  );
}

export default function PnlCalendar() {
  const [kind, setKind] = useState("trade");
  const [bts, setBts] = useState([]);
  const [bt, setBt] = useState("");
  const [rows, setRows] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => { api.get("/runs").then((r) => { const b = r.data.filter((x) => x.type === "backtest"); setBts(b); if (b[0]) setBt(b[0].id); }); }, []);
  useEffect(() => {
    if (kind === "backtest" && !bt) { setRows([]); return; }
    const q = kind === "backtest" ? `backtest_id=${bt}` : `mode=${kind}`;
    api.get(`/pnl/calendar?${q}`).then((r) => {
      setRows(r.data);
      if (r.data.length && !r.data.some((d) => ym(d.date) === month)) setMonth(ym(r.data[r.data.length - 1].date));
    }).catch((e) => toast.error(errMsg(e)));
  }, [kind, bt]); // eslint-disable-line react-hooks/exhaustive-deps

  const byDay = useMemo(() => Object.fromEntries((rows || []).map((d) => [d.date, d])), [rows]);
  const inMonth = (rows || []).filter((d) => ym(d.date) === month);
  const max = Math.max(0, ...inMonth.map((d) => Math.abs(d.net)));
  const net = inMonth.reduce((a, d) => a + d.net, 0);
  const won = inMonth.filter((d) => d.net > 0).length, lost = inMonth.filter((d) => d.net < 0).length;
  const total = (rows || []).reduce((a, d) => a + d.net, 0);
  const pdf = () => {
    const q = kind === "backtest" ? `backtest_id=${bt}` : `mode=${kind}`;
    download(`/pnl/report.pdf?month=${month}&${q}`, `lvrc_pnl_${month}_${kind}.pdf`).then(() => toast.success("Report downloaded")).catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div className="space-y-4" data-testid="pnl-calendar-page">
      <div className="flex flex-wrap items-center gap-3">
        <CalendarDays size={18} className="text-cyan-300" />
        <h1 className="text-xl font-semibold text-slate-100 mr-auto">P&L calendar</h1>
        <div className="w-72"><Seg value={kind} onChange={setKind} testId="cal-kind"
          options={[{ id: "trade", label: "Trade (real)" }, { id: "paper", label: "Paper" }, { id: "backtest", label: "Backtest" }]} /></div>
        {kind === "backtest" && (
          <select value={bt} onChange={(e) => setBt(e.target.value)} data-testid="cal-backtest-select"
            className="h-9 rounded-md bg-[#0B111D] border border-[#1E293B] px-2 text-xs num text-slate-200 max-w-xs">
            {!bts.length && <option value="">No backtests yet</option>}
            {bts.map((b) => <option key={b.id} value={b.id}>{b.date_from} → {b.date_to} · {b.source === "kite" ? "Kite" : "SAMPLE"} · {inr(b.summary?.net)}</option>)}
          </select>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="panel px-3 py-2"><div className="label-xs">{label(month)}</div><div className={`num text-lg ${sign(net)}`} data-testid="cal-month-net">{inr(net)}</div></div>
        <div className="panel px-3 py-2"><div className="label-xs">Green / red days</div><div className="num text-lg"><span className="up">{won}</span> <span className="text-slate-500">/</span> <span className="down">{lost}</span></div></div>
        <div className="panel px-3 py-2"><div className="label-xs">Trading days</div><div className="num text-lg text-slate-100">{inMonth.length}</div></div>
        <div className="panel px-3 py-2"><div className="label-xs">All time ({rows?.length || 0} days)</div><div className={`num text-lg ${sign(total)}`} data-testid="cal-total-net">{inr(total)}</div></div>
      </div>
      <div className="panel p-3">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => setMonth(shift(month, -1))} className="h-8 w-8 rounded-md border border-[#1E293B] grid place-items-center text-slate-300 hover:text-white" data-testid="cal-prev"><ChevronLeft size={14} /></button>
          <span className="text-sm text-slate-100 w-40 text-center" data-testid="cal-month-label">{label(month)}</span>
          <button onClick={() => setMonth(shift(month, 1))} className="h-8 w-8 rounded-md border border-[#1E293B] grid place-items-center text-slate-300 hover:text-white" data-testid="cal-next"><ChevronRight size={14} /></button>
          {kind !== "backtest" && <span className="text-[11px] text-slate-500 hidden md:inline">Kite live runs only · replays and SAMPLE runs are not counted</span>}
          <button onClick={pdf} disabled={kind === "backtest" && !bt} data-testid="cal-report-pdf-btn"
            className="ml-auto h-8 px-3 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40">
            <FileDown size={13} />Monthly report PDF
          </button>
        </div>
        <div className="grid grid-cols-5 gap-1.5 text-[10px] label-xs mb-1">{["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => <div key={d}>{d}</div>)}</div>
        <div className="space-y-1.5">
          {weeks(month).map((w, i) => <div key={i} className="grid grid-cols-5 gap-1.5">{w.map((d, j) => <DayCell key={j} day={d} v={d && byDay[d.iso]} max={max} />)}</div>)}
        </div>
        {rows && !rows.length && <p className="text-xs text-slate-500 mt-3" data-testid="cal-empty">No {kind === "backtest" ? "backtest" : kind} days yet. {kind !== "backtest" && <Link to="/" className="text-cyan-300 hover:underline">Start a run</Link>}</p>}
      </div>
    </div>
  );
}
